import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import { Send, MessageSquare, Loader2, ChevronLeft, Circle } from "lucide-react";
import { i18nLocale } from "../lib/locale";

const fmtTime = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString(i18nLocale(), { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString(i18nLocale(), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function Messages() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(params.get("user") || null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const endRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadThread = useCallback(async (otherId) => {
    if (!otherId) return;
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/conversations/${otherId}/messages`);
      setThread(data);
      // Update the unread counter locally
      setConversations((cs) =>
        cs.map((c) => (c.other_user?.id === otherId ? { ...c, unread: 0 } : c))
      );
    } catch (e) { /* ignore */ }
    setLoadingThread(false);
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { if (activeId) loadThread(activeId); }, [activeId, loadThread]);

  // Auto-scroll to bottom when thread changes
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  // Auto-refresh conversations every 15s
  useEffect(() => {
    const id = setInterval(loadConversations, 15000);
    return () => clearInterval(id);
  }, [loadConversations]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, { text });
      setThread((th) => (th ? { ...th, messages: [...th.messages, data] } : th));
      setText("");
      loadConversations();
    } catch (err) {
      // surface
      alert("Envoi impossible: " + (err.response?.data?.detail || err.message));
    }
    setSending(false);
  };

  const onPickConv = (otherId) => {
    setActiveId(otherId);
    setParams({ user: otherId });
  };

  const isPharmacy = user?.role === "pharmacy";

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      {isPharmacy && <Sidebar />}
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-4 lg:p-10 pb-24 lg:pb-10">
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight mb-6" style={{ fontFamily: "Outfit" }}>
            {t("nav.messages")}
          </h1>

          {conversations.length === 0 && !activeId ? (
            <div className="bg-white rounded-2xl border border-[#E2E5E0] p-12 text-center" data-testid="messages-empty">
              <MessageSquare className="w-10 h-10 text-[#6B726A] mx-auto mb-3" />
              <h3 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Aucune conversation</h3>
              <p className="text-sm text-[#6B726A] mt-1">
                {isPharmacy
                  ? "Contactez un candidat depuis la page Candidats pour démarrer une conversation."
                  : "Vous serez contacté par une pharmacie dès qu'elle vous proposera une mission."}
              </p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[320px_1fr] gap-4">
              {/* Conversations list */}
              <aside className={`lg:block ${activeId ? "hidden" : "block"}`} data-testid="conversations-list">
                <div className="bg-white rounded-2xl border border-[#E2E5E0] overflow-hidden">
                  {conversations.map((c) => {
                    const o = c.other_user || {};
                    const isActive = activeId === o.id;
                    return (
                      <button key={c.conversation_id}
                        data-testid={`conv-row-${o.id}`}
                        onClick={() => onPickConv(o.id)}
                        className={`w-full text-left p-4 border-b border-[#E2E5E0] last:border-b-0 transition-colors ${
                          isActive ? "bg-[#E8F0EA] border-l-4 border-l-[#1A4D2E]" : "hover:bg-[#F7F8F6] border-l-4 border-l-transparent"
                        }`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#E8F0EA] text-[#1A4D2E] grid place-items-center text-sm font-medium shrink-0">
                            {(o.pharmacy_name || o.name || "?")[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm truncate">{o.pharmacy_name || o.name}</span>
                              {c.unread > 0 && (
                                <span className="text-[10px] bg-[#E3655B] text-white rounded-full px-1.5 min-w-[18px] h-[18px] grid place-items-center font-semibold" data-testid={`conv-unread-${o.id}`}>{c.unread}</span>
                              )}
                            </div>
                            <div className="text-xs text-[#6B726A] truncate">{c.last_message?.text}</div>
                            <div className="text-[10px] text-[#9AA098] mt-0.5">{fmtTime(c.last_message?.created_at)}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </aside>

              {/* Thread */}
              <section className={`lg:block ${activeId ? "block" : "hidden"}`} data-testid="thread-pane">
                {activeId ? (
                  <div className="bg-white rounded-2xl border border-[#E2E5E0] flex flex-col h-[calc(100vh-180px)] lg:h-[calc(100vh-220px)]">
                    {/* Header */}
                    <div className="p-4 border-b border-[#E2E5E0] flex items-center gap-3">
                      <button onClick={() => { setActiveId(null); setParams({}); }} className="lg:hidden p-1 text-[#6B726A]" data-testid="thread-back">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="w-9 h-9 rounded-full bg-[#E8F0EA] text-[#1A4D2E] grid place-items-center text-sm font-medium">
                        {(thread?.other_user?.pharmacy_name || thread?.other_user?.name || "?")[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-sm" data-testid="thread-other-name">
                          {thread?.other_user?.pharmacy_name || thread?.other_user?.name || "…"}
                        </div>
                        <div className="text-xs text-[#6B726A]">{thread?.other_user?.professional_role || (thread?.other_user?.role === "pharmacy" ? "Pharmacie" : "Candidat")}</div>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="thread-messages">
                      {loadingThread && (
                        <div className="grid place-items-center h-full text-[#6B726A]"><Loader2 className="w-5 h-5 animate-spin" /></div>
                      )}
                      {!loadingThread && thread?.messages?.map((m) => {
                        const mine = m.sender_id === user.id;
                        return (
                          <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm ${
                              mine ? "bg-[#1A4D2E] text-white rounded-br-md" : "bg-[#F0F2EF] text-[#1f2421] rounded-bl-md"
                            }`} data-testid={`msg-${m.id}`}>
                              <div className="whitespace-pre-wrap break-words">{m.text}</div>
                              <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[#6B726A]"}`}>
                                {fmtTime(m.created_at)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {!loadingThread && thread?.messages?.length === 0 && (
                        <div className="text-center text-sm text-[#6B726A] py-12">
                          Démarrez la conversation 👋
                        </div>
                      )}
                      <div ref={endRef} />
                    </div>

                    {/* Composer */}
                    <form onSubmit={send} className="p-3 border-t border-[#E2E5E0] flex items-center gap-2" data-testid="composer">
                      <input
                        value={text} onChange={(e) => setText(e.target.value)}
                        placeholder="Écrire un message…"
                        className="flex-1 bg-[#F7F8F6] border border-[#E2E5E0] rounded-full px-4 py-2 text-sm outline-none focus:border-[#1A4D2E]"
                        data-testid="composer-input"
                      />
                      <button type="submit" disabled={!text.trim() || sending}
                        data-testid="composer-send"
                        className="w-10 h-10 rounded-full bg-[#1A4D2E] text-white grid place-items-center disabled:opacity-50 hover:bg-[#143C24]">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-[#E2E5E0] p-12 text-center text-sm text-[#6B726A]">
                    Sélectionnez une conversation pour démarrer.
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
